import React, { useEffect, useRef } from "react";

const AddressAutocomplete = ({
  onSelect,
  value = "",
  placeholder = "Enter address",
  showToast,
  className = "w-full",
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const placeSelectedRef = useRef(false);
  const lastCommittedValueRef = useRef("");

  useEffect(() => {
    if (!window.google || !inputRef.current) return;

    autocompleteRef.current =
      new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["address"],
      });

    autocompleteRef.current.setFields([
      "address_components",
      "formatted_address",
    ]);

    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();
      if (!place?.address_components) return;

      const address = {
        full: place.formatted_address,
        street: "",
        route: "",
        city: "",
        state: "",
        zip: "",
        country: "",
      };

      place.address_components.forEach((comp) => {
        const types = comp.types;
        if (types.includes("street_number"))
          address.street = comp.long_name + " " + address.street;
        if (types.includes("route")) address.route += comp.long_name;
        if (types.includes("locality")) address.city = comp.long_name;
        if (types.includes("administrative_area_level_1"))
          address.state = comp.long_name;
        if (types.includes("postal_code")) address.zip = comp.long_name;
        if (types.includes("country")) address.country = comp.long_name;
      });

      placeSelectedRef.current = true;

      // ✅ IMMEDIATE visual update (no flicker)
      inputRef.current.value = address.full;
      lastCommittedValueRef.current = address.full;

      onSelect(address);
    });
  }, []);

  // 🔹 Sync external value ONLY when it truly changes
  useEffect(() => {
    if (!inputRef.current) return;

    if (
      value &&
      value !== lastCommittedValueRef.current &&
      value !== inputRef.current.value
    ) {
      inputRef.current.value = value;
      lastCommittedValueRef.current = value;
    }

    if (!value && inputRef.current.value) {
      inputRef.current.value = "";
      lastCommittedValueRef.current = "";
    }
  }, [value]);

  // User starts typing → invalidate selection
  const handleChange = () => {
    placeSelectedRef.current = false;
  };

  // 🔥 Clean blur — no timeout needed
  const handleBlur = () => {
    const currentValue = inputRef.current.value.trim();

    if (!currentValue) {
      onSelect(null);
      lastCommittedValueRef.current = "";
      return;
    }

    if (!placeSelectedRef.current) {
      showToast?.("Please select an address from suggestions.", "error");
      inputRef.current.value = "";
      lastCommittedValueRef.current = "";
      onSelect(null);
    }

    placeSelectedRef.current = false;
  };

  return (
    <textarea
      ref={inputRef}
      rows={3}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={handleBlur}
      className={`border p-2 rounded-md shadow-sm transition-all ${className}`}
    />
  );
};

export default AddressAutocomplete;